/**
 * ShowFormPage - Create or edit a show
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getShow,
  createShow,
  updateShow,
  uploadShowLogo,
  getAllTourStops,
  formatCurrency,
  type ClassOption,
  type TourStop,
} from '../../../api/admin';

// Must match the show-logos storage bucket's allowed_mime_types (backend validates too).
const ALLOWED_LOGO_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

export default function ShowFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultPrice, setDefaultPrice] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  // True once the admin removes an existing logo, so Save clears logo_url (the
  // remove only updates local state otherwise, and the saved logo would persist).
  const [logoRemoved, setLogoRemoved] = useState(false);
  const [classOptions, setClassOptions] = useState<ClassOption[]>([]);
  const [tourStops, setTourStops] = useState<TourStop[]>([]);
  const [isLoadingTourStops, setIsLoadingTourStops] = useState(false);

  useEffect(() => {
    if (isEditing && id) {
      loadShow(id);
    }
  }, [id, isEditing]);

  const loadShow = async (showId: string) => {
    try {
      setIsLoading(true);
      setIsLoadingTourStops(true);

      // Fetch show data and tour stops in parallel
      const [show] = await Promise.all([
        getShow(showId),
        getAllTourStops({ show_id: showId }).then(({ tour_stops }) => {
          setTourStops(tour_stops);
          setIsLoadingTourStops(false);
        }).catch(() => {
          setTourStops([]);
          setIsLoadingTourStops(false);
        }),
      ]);

      setName(show.name);
      setDescription(show.description || '');
      setDefaultPrice((show.default_base_price_cents / 100).toFixed(2));
      setIsActive(show.is_active);
      setLogoUrl(show.logo_url);
      setClassOptions(show.class_options || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load show');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type — must match what the backend storage bucket accepts.
      if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
        setError('Please use a JPG, PNG, GIF, or WebP image.');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image must be less than 5MB');
        return;
      }
      setLogoFile(file);
      setLogoUrl(URL.createObjectURL(file));
      setLogoRemoved(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!name.trim()) {
      setError('Show name is required');
      return;
    }

    const priceInCents = Math.round(parseFloat(defaultPrice) * 100);
    if (isNaN(priceInCents) || priceInCents <= 0) {
      setError('Please enter a valid default price');
      return;
    }

    try {
      setIsSaving(true);

      let showId = id;

      if (isEditing && id) {
        // Update existing show
        await updateShow(id, {
          name: name.trim(),
          description: description.trim() || undefined,
          default_base_price_cents: priceInCents,
          is_active: isActive,
          // Clear the saved logo when it was removed and no new file replaces it.
          ...(logoRemoved && !logoFile ? { logo_url: null } : {}),
        });
      } else {
        // Create new show - generate slug from name
        const slug = name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        const newShow = await createShow({
          name: name.trim(),
          slug,
          description: description.trim() || undefined,
          default_base_price_cents: priceInCents,
          is_active: isActive,
        });
        showId = newShow.id;
      }

      // Upload logo if selected
      if (logoFile && showId) {
        await uploadShowLogo(showId, logoFile);
      }

      navigate('/admin/shows');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save show');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div>
        <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse mb-8" />
        <div className="bg-white rounded-xl shadow-sm p-8 animate-pulse">
          <div className="space-y-6">
            <div className="h-10 bg-warm-100 rounded w-full" />
            <div className="h-32 bg-warm-100 rounded w-full" />
            <div className="h-10 bg-warm-100 rounded w-1/2" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/admin/shows"
          className="inline-flex items-center text-sm text-warm-600 hover:text-warm-900 mb-4"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Shows
        </Link>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">
          {isEditing ? 'Edit Show' : 'Add New Show'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Basic Information
              </h2>

              {error && (
                <div className="mb-4 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Show Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="e.g., Hamilton"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                    placeholder="Brief description of the show..."
                  />
                </div>

                {/* Default Price */}
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">
                    Default Class Price *
                  </label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-warm-500">
                      $
                    </span>
                    <input
                      type="number"
                      value={defaultPrice}
                      onChange={(e) => setDefaultPrice(e.target.value)}
                      required
                      min="0"
                      step="0.01"
                      className="w-full pl-8 pr-4 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      placeholder="750.00"
                    />
                  </div>
                  <p className="mt-1 text-sm text-warm-500">
                    Base price per class (before travel surcharges)
                  </p>
                </div>

              </div>
            </div>

            {/* Class Options (if editing) */}
            {isEditing && classOptions.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-warm-900 mb-4">
                  Class Options
                </h2>
                <div className="space-y-3">
                  {classOptions.map((option) => (
                    <div
                      key={option.id}
                      className="flex items-center justify-between p-4 bg-warm-50 rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-warm-900">{option.name}</p>
                        <p className="text-sm text-warm-500">{option.description}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-warm-900">
                          {formatCurrency(option.price_cents)}
                        </p>
                        <p className="text-sm text-warm-500">
                          {option.duration_minutes} min
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Tour Stops (if editing) */}
            {isEditing && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-warm-900">
                    Tour Stops{!isLoadingTourStops && ` (${tourStops.length})`}
                  </h2>
                  <Link
                    to="/admin/tour-stops/new"
                    className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                  >
                    + Add Tour Stop
                  </Link>
                </div>

                {isLoadingTourStops ? (
                  <div className="space-y-3">
                    {[1, 2].map((i) => (
                      <div key={i} className="p-4 bg-warm-50 rounded-lg animate-pulse">
                        <div className="h-4 bg-warm-200 rounded w-1/3 mb-2" />
                        <div className="h-3 bg-warm-200 rounded w-1/2" />
                      </div>
                    ))}
                  </div>
                ) : tourStops.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-10 h-10 text-warm-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                    </svg>
                    <p className="text-sm text-warm-500 mb-2">No tour stops yet</p>
                    <Link
                      to="/admin/tour-stops/new"
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      Create the first tour stop
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {tourStops.map((stop) => (
                      <div
                        key={stop.id}
                        className="p-4 bg-warm-50 rounded-lg"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                          <div className="min-w-0">
                            <p className="font-medium text-warm-900 truncate">
                              {stop.theater?.name || 'Unknown Venue'}
                            </p>
                            <p className="text-sm text-warm-500">
                              {stop.city?.name}{stop.city?.state ? `, ${stop.city.state}` : ''}
                            </p>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-sm shrink-0">
                            <span className="text-warm-600">
                              {new Date(stop.start_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              {' — '}
                              {new Date(stop.end_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                            {stop.booking_count !== undefined && stop.booking_count > 0 ? (
                              <Link
                                to={`/admin/bookings?tour_stop=${stop.id}`}
                                className="text-primary-600 hover:text-primary-700 font-medium"
                              >
                                {stop.booking_count} booking{stop.booking_count !== 1 ? 's' : ''}
                              </Link>
                            ) : (
                              <span className="text-warm-400">0 bookings</span>
                            )}
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                              stop.is_sold_out
                                ? 'bg-amber-100 text-amber-700'
                                : stop.is_active
                                ? 'bg-success-100 text-success-700'
                                : 'bg-warm-200 text-warm-600'
                            }`}>
                              {stop.is_sold_out ? 'Sold Out' : stop.is_active ? 'Active' : 'Inactive'}
                            </span>
                            <Link
                              to={`/admin/tour-stops/${stop.id}/edit`}
                              className="text-primary-600 hover:text-primary-700 font-medium"
                            >
                              Edit
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="order-first lg:order-none space-y-6">
            {/* Logo Upload */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Show Logo
              </h2>
              <div className="space-y-4">
                {logoUrl ? (
                  <div className="relative">
                    <img
                      src={logoUrl}
                      alt="Show logo"
                      className="w-full aspect-[3/2] object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setLogoUrl(null);
                        setLogoFile(null);
                        setLogoRemoved(true);
                      }}
                      className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-warm-50"
                    >
                      <svg className="w-4 h-4 text-warm-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center w-full aspect-[3/2] border-2 border-dashed border-warm-300 rounded-lg cursor-pointer hover:border-primary-500 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <svg className="w-10 h-10 text-warm-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-sm text-warm-600 text-center">
                        Click to upload logo
                      </p>
                      <p className="text-xs text-warm-400 mt-1 text-center">
                        JPG, PNG, GIF or WebP
                      </p>
                    </div>
                    <input
                      type="file"
                      accept={ALLOWED_LOGO_TYPES.join(',')}
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </label>
                )}
                <p className="text-xs text-warm-500">
                  JPG, PNG, GIF, or WebP · <strong>3:2 landscape recommended
                  (e.g. 1200×800px)</strong> · max 5MB. This preview matches the
                  search card studios see — images are cropped to fill 3:2.
                </p>
              </div>
            </div>

            {/* Status */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-lg font-semibold text-warm-900 mb-4">
                Status
              </h2>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="w-5 h-5 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-warm-700">Show is active</span>
              </label>
              <p className="mt-2 text-sm text-warm-500">
                Inactive shows won't appear in search results
              </p>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="w-full btn-primary"
                >
                  {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Show'}
                </button>
                <Link
                  to="/admin/shows"
                  className="block w-full text-center px-4 py-2.5 border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
                >
                  Cancel
                </Link>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
